import { useCallback } from "react";
import { FileRejection } from "react-dropzone";

import { useAlertContext } from "@shared";
import { getUploadErrorMessage, notifyRejections } from "@shared";
import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";

import { useTranslation } from "@/translations";

import { useUploadFormatPrompt } from "./useUploadFormatPrompt";

interface UploadVariables {
  file: File;
  convertToPdf: boolean;
}

interface UseDropzoneUploadMutationConfig {
  mutationKey: unknown[];
  uploadFn: (file: File, convertToPdf: boolean) => Promise<void>;
  invalidationKeys: unknown[][];
}

export function useDropzoneUploadMutation({
  mutationKey,
  uploadFn,
  invalidationKeys,
}: UseDropzoneUploadMutationConfig) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();

  const uploadMutation = useMutation({
    mutationKey,
    mutationFn: ({ file, convertToPdf }: UploadVariables) => uploadFn(file, convertToPdf),
    onSuccess: (_data, { file }) => {
      showSuccess(t("dropzone.upload.success", { fileName: file.name }));
      for (const key of invalidationKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: async (error: unknown, { file }) => {
      showError(await getUploadErrorMessage(error, file.name, t));
    },
  });

  const upload = useCallback(
    (file: File, convertToPdf: boolean) => uploadMutation.mutate({ file, convertToPdf }),
    [uploadMutation]
  );

  const { promptOrUpload, formatDialog } = useUploadFormatPrompt(upload);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      notifyRejections(rejectedFiles, showError, t);
      promptOrUpload(acceptedFiles);
    },
    [promptOrUpload, showError, t]
  );

  const uploadingFiles = useMutationState({
    filters: { mutationKey, status: "pending" },
    select: (mutation) => (mutation.state.variables as UploadVariables).file,
  });

  return { onDrop, uploadingFiles, formatDialog };
}
